/**
 * Curtain — a full-screen two-slab wipe that covers the viewport,
 * holds, and uncovers in one continuous direction.
 *
 * Generic and brand-agnostic: pass any two Tailwind/CSS classes for the
 * slab colors. Useful for hiding any layout reflow during a state swap
 * (i18n language toggle, theme change, route morph, etc.) — orchestrate
 * the swap on the parent's side and toggle `phase` to run the wipe.
 *
 * Phases (parent-driven):
 *   idle       — slabs off-screen on the entry side
 *   covering   — slabs slide in toward 0%
 *   covered    — slabs at 0%; do the swap during this phase
 *   uncovering — slabs slide off-screen on the exit side
 *
 * On entry the leading slab moves first so a contrasting lead color
 * reads as a "ribbon" at the wipe's edge. On exit both slabs slide off
 * synchronously — the trailing slab stays on top, fully covering the
 * leading slab so the lead color never re-appears (matches the burger
 * menu's open/close gesture).
 */
export type CurtainPhase = 'idle' | 'covering' | 'covered' | 'uncovering'

/** Default per-slab slide duration, ms. */
export const CURTAIN_SLIDE_MS = 250

/** Default offset between leading and trailing slab, ms. */
export const CURTAIN_STAGGER_MS = 70

/**
 * Total time `covering` takes to reach full coverage. Use this in the
 * parent to schedule the phase transition `covering` → `covered`.
 */
export const CURTAIN_COVER_MS = CURTAIN_SLIDE_MS + CURTAIN_STAGGER_MS

/**
 * Total time `uncovering` takes to fully clear the viewport. Use this
 * in the parent to schedule `uncovering` → `idle`.
 */
export const CURTAIN_UNCOVER_MS = CURTAIN_SLIDE_MS + CURTAIN_STAGGER_MS

const DEFAULT_EASE = 'cubic-bezier(0.76, 0, 0.24, 1)'

interface CurtainProps {
  phase: CurtainPhase
  /** Leading slab color class (e.g. `'bg-accent'`). Renders below the trail slab. */
  leadClassName: string
  /** Trailing slab color class (e.g. `'bg-ink'`). Renders on top. */
  trailClassName: string
  /** Wipe direction. `ltr` enters from the left and exits to the right. Default `ltr`. */
  direction?: 'ltr' | 'rtl'
  /** Per-slab slide duration, ms. Default 250. */
  slideMs?: number
  /** Offset between leading and trailing slab, ms. Default 70. */
  staggerMs?: number
  /** CSS easing function. Default `cubic-bezier(0.76, 0, 0.24, 1)`. */
  ease?: string
  /** z-index of the overlay. Default 200. */
  zIndex?: number
  className?: string
}

interface SlabStyle {
  transform: string
  transitionProperty: string
  transitionDuration: string
  transitionTimingFunction: string
}

function styleFor(
  phase: CurtainPhase,
  isLeading: boolean,
  direction: 'ltr' | 'rtl',
  slideMs: number,
  staggerMs: number,
  ease: string,
): SlabStyle {
  const offEntry = direction === 'ltr' ? '-100%' : '100%'
  const offExit = direction === 'ltr' ? '100%' : '-100%'

  let translate = offEntry
  let duration = '0ms'
  let property = 'none'

  if (phase === 'covering') {
    translate = '0%'
    // Entry stagger via duration. Both slabs start simultaneously at
    // t=0; leading slab finishes first (slideMs), trailing slab
    // finishes later (slideMs + staggerMs). The leading slab's
    // shorter duration means it reaches the cover line ahead of trail
    // → produces the lead-color ribbon at the wipe's leading edge.
    duration = isLeading ? `${slideMs}ms` : `${slideMs + staggerMs}ms`
    property = 'transform'
  } else if (phase === 'covered') {
    translate = '0%'
    // Hard reset between cover and uncover: cancel any in-flight
    // transition. Without this, Chromium carries staggered cover
    // progress into the uncover transition — the trailing slab
    // arrives at +100% ahead of the leading slab, exposing the lead
    // color on exit. With transition-property: none here, both slabs
    // are guaranteed to be parked at 0% before uncover begins.
    duration = '0ms'
    property = 'none'
  } else if (phase === 'uncovering') {
    translate = offExit
    // Exit: identical duration on both slabs → they slide together in
    // lockstep, the trailing slab covering the leading slab throughout.
    duration = `${slideMs}ms`
    property = 'transform'
  }

  return {
    transform: `translateX(${translate})`,
    transitionProperty: property,
    transitionDuration: duration,
    transitionTimingFunction: ease,
  }
}

export function Curtain({
  phase,
  leadClassName,
  trailClassName,
  direction = 'ltr',
  slideMs = CURTAIN_SLIDE_MS,
  staggerMs = CURTAIN_STAGGER_MS,
  ease = DEFAULT_EASE,
  zIndex = 200,
  className = '',
}: CurtainProps) {
  return (
    <div
      aria-hidden="true"
      data-testid="curtain"
      data-phase={phase}
      className={`pointer-events-none fixed inset-0 overflow-hidden ${className}`}
      style={{ zIndex }}
    >
      {/* Explicit z-index so the trail slab is always on top — both
          slabs create stacking contexts via will-change, and we don't
          want their relative order to depend on browser composite
          ordering quirks. */}
      <div
        data-slab="lead"
        className={`absolute inset-0 z-[1] will-change-transform ${leadClassName}`}
        style={styleFor(phase, true, direction, slideMs, staggerMs, ease)}
      />
      <div
        data-slab="trail"
        className={`absolute inset-0 z-[2] will-change-transform ${trailClassName}`}
        style={styleFor(phase, false, direction, slideMs, staggerMs, ease)}
      />
    </div>
  )
}
